import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { formatearFecha } from '@/lib/fecha'
import { formatearRNC } from '@/lib/rnc'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { Monto } from '@/ui/Numero'
import { DialogoConMotivo } from '@/consola/DialogoConMotivo'
import { traducirErrorConsola } from '@/dominio/consola/useConsola'
import { PRESENTACION_ETAPA, monedaPresentable } from '@/dominio/cuenta/tipos'
import { useGenerarCargosDelMes, useMorosos } from '@/dominio/cobros/useCobros'
import { EncabezadoPantalla, Tarjeta } from '../-pantalla'
import { ErrorDeCarga, SoloDueno, entero, useEsDueno } from './-comun'

/**
 * Los clientes que le deben a Agentical Group (`docs/adr/0020` §4): en gracia,
 * restringidos y suspendidos, del más atrasado al menos; y, si se pide, los que
 * vencen en los próximos días.
 *
 * La etapa la calcula la base con la misma función que usa el filtro de la
 * emisión, así que lo que dice esta lista es exactamente lo que el cliente está
 * viviendo. Cada fila lleva a su pestaña de Cobros, donde se registra el pago o
 * se concede la prórroga.
 *
 * Aquí también está **Generar cargos del mes**: el proceso diario corre solo,
 * y este botón existe para cuando los trabajadores están apagados o no se
 * quiere esperar a su vuelta. Es idempotente como él.
 */
export const Route = createFileRoute('/consola/morosos')({
  validateSearch: (busqueda: Record<string, unknown>): { porVencer?: boolean } => ({
    porVencer: busqueda.porVencer === true || busqueda.porVencer === 'true' ? true : undefined,
  }),
  component: PantallaMorosos,
})

function PantallaMorosos() {
  const { porVencer = false } = Route.useSearch()
  const navegar = Route.useNavigate()
  const dueno = useEsDueno()
  const consulta = useMorosos(porVencer)
  const [generando, setGenerando] = useState(false)
  const generar = useGenerarCargosDelMes()
  const th = 'h-[var(--alto-fila)] px-2 text-left text-etiqueta font-semibold text-gris-600'
  const filas = consulta.data ?? []

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Morosos"
        descripcion="Clientes con pagos de la suscripción vencidos. En gracia todo les funciona; restringidos no emiten comprobantes nuevos; suspendidos solo pagan y exportan."
        acciones={
          dueno ? (
            <Boton variante="secundario" onClick={() => setGenerando(true)}>
              Generar cargos del mes
            </Boton>
          ) : (
            <SoloDueno />
          )
        }
      />

      {generar.isSuccess ? (
        <Banner tono="verde" titulo="Cargos del mes generados">
          {entero(generar.data.creados)} cargo(s) nuevos para {entero(generar.data.tenants)} cliente(s) con algo que
          cobrar{generar.data.fallidos > 0 ? `; ${entero(generar.data.fallidos)} con fallo, en la bitácora` : ''}.
        </Banner>
      ) : null}

      <label className="flex w-fit items-center gap-2 text-cuerpo text-gris-700">
        <input
          type="checkbox"
          checked={porVencer}
          onChange={(e) => void navegar({ search: { porVencer: e.target.checked ? true : undefined } })}
        />
        Incluir los que vencen en los próximos días
      </label>

      <Tarjeta relleno={false}>
        {consulta.isError ? (
          <ErrorDeCarga error={consulta.error} queCargaba="los morosos" onReintentar={() => void consulta.refetch()} />
        ) : !consulta.isLoading && filas.length === 0 ? (
          <EstadoVacio variante="resuelto" mensaje="Nadie tiene pagos vencidos." />
        ) : (
          <table className="w-full border-collapse text-cuerpo" data-densidad="normal">
            <thead className="bg-gris-50">
              <tr className="border-b border-gris-300">
                <th scope="col" className={th}>
                  Cliente
                </th>
                <th scope="col" className={`${th} w-[140px]`}>
                  Etapa
                </th>
                <th scope="col" className={`${th} w-[110px]`}>
                  Venció
                </th>
                <th scope="col" className={`${th} w-[90px] text-right`}>
                  Atraso
                </th>
                <th scope="col" className={`${th} w-[140px] text-right`}>
                  Debe
                </th>
                <th scope="col" className={`${th} w-[90px] text-right`}>
                  Cargos
                </th>
                <th scope="col" className={`${th} w-[130px]`}>
                  Etapa hasta
                </th>
              </tr>
            </thead>
            <tbody>
              {consulta.isLoading ? (
                <FilasEsqueleto filas={5} columnas={7} />
              ) : (
                filas.map((f) => {
                  const etapa = PRESENTACION_ETAPA[f.etapa]
                  return (
                    <tr key={f.tenantId} className="h-[var(--alto-fila)] border-b border-gris-200 last:border-b-0">
                      <td className="px-2">
                        <Link
                          to="/consola/clientes/$id"
                          params={{ id: f.tenantId }}
                          search={{ pestana: 'cobros' }}
                          className="flex min-w-0 flex-col hover:text-marca"
                        >
                          <span className="truncate text-gris-900">{f.razonSocial}</span>
                          <span className="numero text-etiqueta text-gris-600">RNC {formatearRNC(f.rnc)}</span>
                        </Link>
                      </td>
                      <td className="px-2">
                        <InsigniaEstado tono={etapa.tono} aclaracion={f.causa === 'prueba' ? 'prueba' : undefined}>
                          {etapa.etiqueta}
                        </InsigniaEstado>
                      </td>
                      <td className="numero px-2 text-gris-700">{f.venceEl ? formatearFecha(f.venceEl) : '—'}</td>
                      <td className="numero px-2 text-right text-gris-900">
                        {f.diasAtraso === null ? '—' : `${Math.max(0, f.diasAtraso)} d`}
                      </td>
                      <td className="px-2 text-right">
                        <Monto valor={f.saldo} moneda={monedaPresentable(f.moneda)} />
                      </td>
                      <td className="numero px-2 text-right text-gris-700">{entero(f.cargosPendientes)}</td>
                      <td className="numero px-2 text-gris-700">{f.hasta ? formatearFecha(f.hasta) : '—'}</td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        )}
      </Tarjeta>

      {generando ? (
        <DialogoConMotivo
          abierto
          onCancelar={() => setGenerando(false)}
          onConfirmar={(motivo) => generar.mutate(motivo, { onSuccess: () => setGenerando(false) })}
          titulo="Generar los cargos del mes"
          descripcion={
            <p>
              El cargo de este mes de cada cliente con suscripción activa, y de la caja y la nómina donde hay tarifa. Lo
              que ya existe —o se anuló— no se vuelve a crear, así que repetirlo no cobra dos veces.
            </p>
          }
          etiquetaConfirmar="Generar"
          trabajando={generar.isPending}
          error={generar.isError ? traducirErrorConsola(generar.error, 'generar los cargos del mes') : null}
        />
      ) : null}
    </div>
  )
}
